import { useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { TenantPhoto } from '../api';

interface PhotoGalleryProps {
  photos: TenantPhoto[];
}

function Lightbox({
  photos,
  initialIndex,
  onClose,
}: {
  photos: TenantPhoto[];
  initialIndex: number;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(initialIndex);

  const prev = () => setIndex((i) => (i > 0 ? i - 1 : photos.length - 1));
  const next = () => setIndex((i) => (i < photos.length - 1 ? i + 1 : 0));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 rounded-full bg-white/10 p-2 text-white backdrop-blur-sm transition-colors hover:bg-white/20"
      >
        <X className="h-6 w-6" />
      </button>

      {photos.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className="absolute left-4 rounded-full bg-white/10 p-2 text-white backdrop-blur-sm transition-colors hover:bg-white/20"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className="absolute right-4 rounded-full bg-white/10 p-2 text-white backdrop-blur-sm transition-colors hover:bg-white/20"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      <img
        src={photos[index].image}
        alt={`Photo ${index + 1}`}
        className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain"
        onClick={(e) => e.stopPropagation()}
      />

      {photos.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-sm text-white backdrop-blur-sm">
          {index + 1} / {photos.length}
        </div>
      )}
    </div>
  );
}

export default function PhotoGallery({ photos }: PhotoGalleryProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [carouselIndex, setCarouselIndex] = useState(0);

  if (photos.length === 0) return null;

  const count = photos.length;

  return (
    <>
      {/* Mobile carousel */}
      <div className="relative md:hidden">
        {count === 1 ? (
          <img
            src={photos[0].image}
            alt="Salon photo"
            className="h-64 w-full cursor-pointer object-cover sm:h-80"
            onClick={() => setLightboxIndex(0)}
          />
        ) : (
          <div className="relative">
            <img
              src={photos[carouselIndex].image}
              alt={`Salon photo ${carouselIndex + 1}`}
              className="h-64 w-full cursor-pointer object-cover sm:h-80"
              onClick={() => setLightboxIndex(carouselIndex)}
            />
            <button
              onClick={() => setCarouselIndex((i) => (i > 0 ? i - 1 : count - 1))}
              className="absolute top-1/2 left-3 -translate-y-1/2 rounded-full bg-white/80 p-1.5 shadow transition-colors hover:bg-white"
            >
              <ChevronLeft className="h-5 w-5 text-gray-800" />
            </button>
            <button
              onClick={() => setCarouselIndex((i) => (i < count - 1 ? i + 1 : 0))}
              className="absolute top-1/2 right-3 -translate-y-1/2 rounded-full bg-white/80 p-1.5 shadow transition-colors hover:bg-white"
            >
              <ChevronRight className="h-5 w-5 text-gray-800" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-2.5 py-0.5 text-xs font-medium text-white">
              {carouselIndex + 1} / {count}
            </div>
          </div>
        )}
      </div>

      {/* Desktop grid layouts */}
      <div className="hidden md:block">
        {count === 1 && (
          <div className="h-96 overflow-hidden">
            <img
              src={photos[0].image}
              alt="Salon photo"
              className="h-full w-full cursor-pointer object-cover"
              onClick={() => setLightboxIndex(0)}
            />
          </div>
        )}

        {count === 2 && (
          <div className="grid h-96 grid-cols-2 gap-1 overflow-hidden">
            {photos.slice(0, 2).map((photo, i) => (
              <div key={photo.id} className="overflow-hidden">
                <img
                  src={photo.image}
                  alt={`Salon photo ${i + 1}`}
                  className="h-full w-full cursor-pointer object-cover"
                  onClick={() => setLightboxIndex(i)}
                />
              </div>
            ))}
          </div>
        )}

        {count === 3 && (
          <div className="grid h-96 grid-cols-2 gap-1 overflow-hidden">
            <div className="overflow-hidden">
              <img
                src={photos[0].image}
                alt="Salon photo 1"
                className="h-full w-full cursor-pointer object-cover"
                onClick={() => setLightboxIndex(0)}
              />
            </div>
            <div className="grid grid-rows-2 gap-1">
              {photos.slice(1, 3).map((photo, i) => (
                <div key={photo.id} className="overflow-hidden">
                  <img
                    src={photo.image}
                    alt={`Salon photo ${i + 2}`}
                    className="h-full w-full cursor-pointer object-cover"
                    onClick={() => setLightboxIndex(i + 1)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {count === 4 && (
          <div className="grid h-96 grid-cols-2 gap-1 overflow-hidden">
            <div className="overflow-hidden">
              <img
                src={photos[0].image}
                alt="Salon photo 1"
                className="h-full w-full cursor-pointer object-cover"
                onClick={() => setLightboxIndex(0)}
              />
            </div>
            <div className="grid grid-cols-2 grid-rows-2 gap-1">
              {photos.slice(1, 4).map((photo, i) => (
                <div key={photo.id} className={`overflow-hidden ${i === 2 ? 'col-span-2' : ''}`}>
                  <img
                    src={photo.image}
                    alt={`Salon photo ${i + 2}`}
                    className="h-full w-full cursor-pointer object-cover"
                    onClick={() => setLightboxIndex(i + 1)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {count >= 5 && (
          <div className="grid h-96 grid-cols-2 gap-1 overflow-hidden">
            <div className="overflow-hidden">
              <img
                src={photos[0].image}
                alt="Salon photo 1"
                className="h-full w-full cursor-pointer object-cover"
                onClick={() => setLightboxIndex(0)}
              />
            </div>
            <div className="grid grid-cols-2 grid-rows-2 gap-1">
              {photos.slice(1, 5).map((photo, i) => (
                <div key={photo.id} className="relative overflow-hidden">
                  <img
                    src={photo.image}
                    alt={`Salon photo ${i + 2}`}
                    className="h-full w-full cursor-pointer object-cover"
                    onClick={() => setLightboxIndex(i + 1)}
                  />
                  {i === 3 && count > 5 && (
                    <button
                      onClick={() => setLightboxIndex(4)}
                      className="absolute inset-0 flex cursor-pointer items-center justify-center bg-black/40 text-sm font-semibold text-white transition-colors hover:bg-black/50"
                    >
                      See all {count} photos
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {lightboxIndex !== null && (
        <Lightbox
          photos={photos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
}
